import { useState, useRef, useEffect, useCallback, useContext, useMemo } from 'react'
import { BarcodeDetector as ZXingBarcodeDetector, prepareZXingModule } from 'barcode-detector'
import type { DetectedBarcode, BarcodeFormat as BarcodeDetectorFormat } from 'barcode-detector'
import Spinner from './Spinner'
import { GlobalContext } from '../GlobalContext'
import Button from '../formfields/Button'
import RedAlert from './RedAlert'



// Serve the WASM decoder (used only on browsers without a native Shape Detection API, e.g.
// Safari/iOS) from our own domain rather than the package's default jsDelivr CDN fetch - the
// binary itself lives in public/wasm/, copied from node_modules/zxing-wasm/dist/reader/.
prepareZXingModule({
    overrides: {
        locateFile: (path: string, prefix: string): string => (path.endsWith('.wasm') ? '/wasm/zxing_reader.wasm' : prefix + path),
    },
})

// Importing 'barcode-detector' registers window.BarcodeDetector as a side effect - native
// implementation if the browser already has one, the WASM-backed class otherwise (Safari has
// no native implementation at all). Reading it back here means we only ever construct/use the
// WASM class on browsers that actually need it - browsers with native support never trigger
// the (~1MB) WASM download.
type BarcodeDetectorConstructor = typeof ZXingBarcodeDetector
const nativeBarcodeDetector = typeof window !== 'undefined'
    ? (window as unknown as { BarcodeDetector?: BarcodeDetectorConstructor }).BarcodeDetector
    : undefined
const ActiveBarcodeDetector: BarcodeDetectorConstructor = nativeBarcodeDetector || ZXingBarcodeDetector
const isUsingNativeBarcodeDetector = !!nativeBarcodeDetector

// Every format the native Shape Detection API supports, so behaviour is consistent whether a
// device ends up using the native implementation or the WASM polyfill (which supports a much
// wider set including some overly specialised ones - parcel shipping labels, grocery
// variable-weight codes - that native doesn't, and that this app doesn't need).
const READABLE_FORMATS: BarcodeDetectorFormat[] = [
    'qr_code',
    'aztec',
    'data_matrix',
    'pdf417',
    'codabar',
    'code_39',
    'code_93',
    'code_128',
    'ean_8',
    'ean_13',
    'itf',
    'upc_a',
    'upc_e',
]

// Formats whose symbols are roughly square (2D matrix codes) rather than a wide horizontal
// strip (1D/linear and stacked codes) - determines whether the on-screen target is a wide
// letterbox or a square, and whether the vertical crosshair bar is shown.
const SQUARE_TARGET_FORMATS: BarcodeDetectorFormat[] = ['aztec', 'qr_code', 'data_matrix']

// The wide (1D/linear) target is expressed as a percentage of the viewport, since it never
// needs to be square. The square (2D matrix) target can't be a fixed percentage of width/
// height independently - the viewport usually isn't square, so 45% of width and 45% of
// height are different pixel counts and the "square" ends up a rectangle. Instead its side
// length is computed at runtime as a fraction of whichever viewport dimension is shorter,
// which is always a true square regardless of orientation or screen shape.
const WIDE_TARGET_SHAPE_PERCENT = { widthPercent: 90, heightPercent: 10 }
const SQUARE_TARGET_SIDE_FRACTION_OF_SHORTEST_SIDE = 0.6

type BarcodeOptionsProp = {
    formats?: Record<string, boolean>
    showTarget?: boolean
}

function formatName(format: string): string {
    return format.split('_').join(' ').toUpperCase()
}

// Works out, in native video pixel coordinates, the rectangle that corresponds to a target
// box of targetSizeCssPx (in on-screen CSS pixels, centred on the viewport). The video
// element is shown with object-cover, which scales the native frame up until it fills the
// display box on whichever axis is relatively narrower, then crops the overflow on the
// other axis - so a given on-screen box isn't simply that many pixels of the native frame
// whenever the camera's native aspect ratio doesn't match the viewport's.
function computeCropRectForTarget(
    video: HTMLVideoElement,
    targetSizeCssPx: { width: number; height: number }
): { cropX: number; cropY: number; cropWidth: number; cropHeight: number } | null {
    const videoWidth = video.videoWidth
    const videoHeight = video.videoHeight
    if (!videoWidth || !videoHeight) return null

    const containerWidth = window.innerWidth
    const containerHeight = window.innerHeight
    // CSS pixels per native video pixel, under object-cover.
    const scale = Math.max(containerWidth / videoWidth, containerHeight / videoHeight)
    const visibleVideoWidth = containerWidth / scale
    const visibleVideoHeight = containerHeight / scale
    const visibleOffsetX = (videoWidth - visibleVideoWidth) / 2
    const visibleOffsetY = (videoHeight - visibleVideoHeight) / 2

    const cropWidth = targetSizeCssPx.width / scale
    const cropHeight = targetSizeCssPx.height / scale
    const cropX = visibleOffsetX + (visibleVideoWidth - cropWidth) / 2
    const cropY = visibleOffsetY + (visibleVideoHeight - cropHeight) / 2

    return { cropX, cropY, cropWidth, cropHeight }
}

// 'focusMode'/'torch' are real, widely-implemented (Chrome/Android) constraints but aren't
// part of TypeScript's DOM lib typings yet.
type ExtendedTrackConstraintSet = MediaTrackConstraintSet & {
    focusMode?: 'continuous' | 'single-shot' | 'manual' | 'none'
    torch?: boolean
}
type ExtendedTrackCapabilities = MediaTrackCapabilities & { torch?: boolean }

// A track's facingMode *setting* is only populated on devices that actually have more than
// one camera to choose between - a laptop webcam typically reports no facingMode capability
// at all, not 'user'. Mirrors VideoProvider.tsx's isFacingUser detection: front-facing if the
// capability explicitly says 'user', or if the camera doesn't report facingMode at all (the
// laptop-webcam case).
function isTrackFacingUser(track: MediaStreamTrack): boolean {
    try {
        const capabilities = track.getCapabilities?.() as (MediaTrackCapabilities & { facingMode?: string[] }) | undefined
        if (!capabilities?.facingMode) return false
        return capabilities.facingMode.includes('user') || capabilities.facingMode.length === 0
    } catch {
        return false
    }
}

function buildVideoConstraints(deviceId?: string): MediaStreamConstraints {
    const source: MediaTrackConstraintSet = deviceId
        ? { deviceId: { exact: deviceId } }
        : { facingMode: 'environment' }
    return {
        video: {
            ...source,
            // A bigger frame gives the decoder more pixels to resolve small/dense
            // codes with - most phone cameras default to a much lower profile
            // unless a resolution is explicitly requested. "ideal" is a soft
            // constraint, so this never fails on cameras that can't reach it.
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            advanced: [{ focusMode: 'continuous' } as ExtendedTrackConstraintSet],
        },
    }
}

function getFriendlyErrorMessage(error: unknown, t: (key: string) => string): string {
    const name = (error as { name?: string } | undefined)?.name
    switch (name) {
        case 'NotAllowedError':
            return t('UIElements.BarCodeReader.permissionDenied')
        case 'NotFoundError':
        case 'OverconstrainedError':
            return t('UIElements.BarCodeReader.noCameraFound')
        case 'NotReadableError':
            return t('UIElements.BarCodeReader.cameraInUse')
        case 'SecurityError':
            return t('UIElements.BarCodeReader.insecureConnection')
        default:
            return (error as { message?: string } | undefined)?.message || t('UIElements.BarCodeReader.unableToAccessCamera')
    }
}

function BarCodeReader({
    results, setResults, barcodeOptions
}:
    {
        results: ScanResult | null
        setResults: React.Dispatch<React.SetStateAction<ScanResult | null>>
        barcodeOptions?: BarcodeOptionsProp
    }) {


    const { t } = useContext(GlobalContext)
    const videoRef = useRef<HTMLVideoElement>(null)
    const freezeCanvasRef = useRef<HTMLCanvasElement>(null)
    const cropCanvasRef = useRef<HTMLCanvasElement | null>(null)
    const decodeBusyRef = useRef(false)
    const detectorRef = useRef<InstanceType<BarcodeDetectorConstructor> | null>(null)
    const streamRef = useRef<MediaStream | null>(null)
    const intervalRef = useRef<number | null>(null)
    const mountedRef = useRef(true)
    const startPendingRef = useRef(false)

    // Which formats this question was configured to look for, and whether it's been set
    // up with a guided target. Unconfigured (legacy) questions keep the old "look for
    // everything, whole picture" behaviour.
    const possibleFormats = useMemo<BarcodeDetectorFormat[]>(() => {
        const formatSettings = barcodeOptions?.formats
        if (!formatSettings) return READABLE_FORMATS
        const enabled = READABLE_FORMATS.filter((format) => formatSettings[format] !== false)
        return enabled.length > 0 ? enabled : READABLE_FORMATS
    }, [barcodeOptions])

    const showTarget = !!barcodeOptions?.showTarget
    const targetIsSquare = useMemo(
        () => possibleFormats.some((format) => SQUARE_TARGET_FORMATS.includes(format)),
        [possibleFormats]
    )

    // Tracked live (not just read once) because the square target's side length depends on
    // it, and orientation changes / window resizes should reshape the target to match.
    const [viewportSize, setViewportSize] = useState({ width: window.innerWidth, height: window.innerHeight })
    useEffect(() => {
        const updateViewportSize = () => setViewportSize({ width: window.innerWidth, height: window.innerHeight })
        window.addEventListener('resize', updateViewportSize)
        return () => window.removeEventListener('resize', updateViewportSize)
    }, [])

    const targetSizeCssPx = useMemo(() => {
        if (targetIsSquare) {
            const side = SQUARE_TARGET_SIDE_FRACTION_OF_SHORTEST_SIDE * Math.min(viewportSize.width, viewportSize.height)
            return { width: side, height: side }
        }
        return {
            width: viewportSize.width * (WIDE_TARGET_SHAPE_PERCENT.widthPercent / 100),
            height: viewportSize.height * (WIDE_TARGET_SHAPE_PERCENT.heightPercent / 100),
        }
    }, [targetIsSquare, viewportSize])

    const [isScanning, setIsScanning] = useState(false)
    const [starting, setStarting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
    const [selectedDeviceId, setSelectedDeviceId] = useState<string>('')
    const [torchOn, setTorchOn] = useState(false)
    const [torchSupported, setTorchSupported] = useState(false)
    const [isFrontFacing, setIsFrontFacing] = useState(false)

    const cameraSupported = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
    const unsupportedReason = !cameraSupported
        ? t('UIElements.BarCodeReader.unsupportedBrowser')
        : null

    const getDetector = () => {
        if (!detectorRef.current) {
            detectorRef.current = new ActiveBarcodeDetector({ formats: possibleFormats })
            console.log(
                `🔍 BarCodeReader: using ${isUsingNativeBarcodeDetector ? 'native' : 'WASM polyfill'} BarcodeDetector, formats:`,
                possibleFormats
            )
        }
        return detectorRef.current
    }

    const stopScanning = useCallback(() => {
        if (intervalRef.current !== null) {
            window.clearInterval(intervalRef.current)
            intervalRef.current = null
        }
        streamRef.current?.getTracks().forEach((track) => track.stop())
        streamRef.current = null
        setIsScanning(false)
        setTorchSupported(false)
        setTorchOn(false)
    }, [])

    const handleDetected = useCallback((detectedBarcodes: DetectedBarcode[]) => {
        const first = detectedBarcodes[0]
        if (!first || !first.rawValue) return

        const format = formatName(first.format)

        // Grab the current frame before stopping - stopScanning() clears the
        // video's srcObject, which would otherwise leave a black square where
        // the camera preview was.
        const video = videoRef.current
        const canvas = freezeCanvasRef.current
        if (video && canvas) {
            canvas.width = video.videoWidth
            canvas.height = video.videoHeight
            canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height)
        }

        setResults({ text: first.rawValue, format, timestamp: Date.now() })
        // Release the camera once a code is found - keeping the stream and
        // decode loop running was driving up browser memory use.
        stopScanning()
    }, [stopScanning, setResults])

    // Runs on an interval while scanning. When showTarget is on, crops to just the guided
    // target rectangle before detecting, so a code sitting outside the window is never
    // matched; otherwise detects straight off the live video, whole frame.
    const scanTick = useCallback(async () => {
        const video = videoRef.current
        if (!video || video.readyState < 2 || decodeBusyRef.current) return

        decodeBusyRef.current = true
        try {
            let source: HTMLVideoElement | HTMLCanvasElement = video

            if (showTarget) {
                const cropRect = computeCropRectForTarget(video, targetSizeCssPx)
                if (!cropRect || cropRect.cropWidth <= 0 || cropRect.cropHeight <= 0) return
                if (!cropCanvasRef.current) {
                    cropCanvasRef.current = document.createElement('canvas')
                }
                const canvas = cropCanvasRef.current
                canvas.width = cropRect.cropWidth
                canvas.height = cropRect.cropHeight
                const ctx = canvas.getContext('2d')
                if (!ctx) return
                ctx.drawImage(
                    video,
                    cropRect.cropX, cropRect.cropY, cropRect.cropWidth, cropRect.cropHeight,
                    0, 0, cropRect.cropWidth, cropRect.cropHeight
                )
                source = canvas
            }

            try {
                const detectedBarcodes = await getDetector().detect(source)
                if (detectedBarcodes.length > 0) handleDetected(detectedBarcodes)
            } catch (detectError) {
                // "Nothing found" resolves to an empty array, not a rejection - a thrown
                // error here is a genuine problem, worth knowing about but not fatal to
                // the scan loop (the next tick just tries again).
                console.error(detectError)
            }
        } finally {
            decodeBusyRef.current = false
        }
    }, [showTarget, targetSizeCssPx, handleDetected])

    const startScanning = useCallback(async (deviceId?: string) => {
        if (!cameraSupported || !videoRef.current) return
        // Guards against overlapping calls (e.g. React StrictMode's dev mount ->
        // cleanup -> mount firing this twice back to back).
        if (startPendingRef.current) return
        startPendingRef.current = true

        setError(null)
        setStarting(true)
        stopScanning()

        try {
            const stream = await navigator.mediaDevices.getUserMedia(buildVideoConstraints(deviceId || undefined))
            if (!mountedRef.current) {
                stream.getTracks().forEach((track) => track.stop())
                return
            }
            streamRef.current = stream
            videoRef.current.srcObject = stream
            try {
                await videoRef.current.play()
            } catch {
                // muted + playsInline should make this always resolve, but some browsers
                // are stricter about autoplay timing - not fatal if it does throw.
            }

            intervalRef.current = window.setInterval(scanTick, 75)

            const videoTrack = stream.getVideoTracks()[0]
            const capabilities = videoTrack?.getCapabilities?.() as ExtendedTrackCapabilities | undefined
            setIsScanning(true)
            setTorchOn(false)
            setTorchSupported(!!capabilities?.torch)
            setIsFrontFacing(videoTrack ? isTrackFacingUser(videoTrack) : false)

            const activeDeviceId = videoTrack?.getSettings().deviceId
            if (activeDeviceId) setSelectedDeviceId(activeDeviceId)

            // Device labels are only populated once permission has been granted,
            // so we (re-)enumerate here rather than before starting the camera.
            try {
                const allDevices = await navigator.mediaDevices.enumerateDevices()
                setDevices(allDevices.filter((device) => device.kind === 'videoinput'))
            } catch {
                // Listing extra cameras is a nice-to-have; scanning still works without it.
            }
        } catch (e) {
            setIsScanning(false)
            setError(getFriendlyErrorMessage(e, t))
        } finally {
            setStarting(false)
            startPendingRef.current = false
        }
    }, [cameraSupported, t, scanTick, stopScanning])


    const switchCameraToNextDevice = useCallback(() => {
        if (devices.length < 2) return
        const currentIndex = devices.findIndex(d => d.deviceId === selectedDeviceId)
        const nextIndex = (currentIndex + 1) % devices.length
        const nextDeviceId = devices[nextIndex].deviceId
        handleCameraChange(nextDeviceId)
    }, [devices, selectedDeviceId])

    const handleCameraChange = (deviceId: string) => {
        setSelectedDeviceId(deviceId)
        if (isScanning) startScanning(deviceId)
    }

    const rescan = useCallback(() => {
        setResults(null)
        startScanning(selectedDeviceId || undefined)
    }, [startScanning, selectedDeviceId])

    const toggleTorch = useCallback(async () => {
        const videoTrack = streamRef.current?.getVideoTracks()[0]
        if (!videoTrack) return
        const next = !torchOn
        try {
            await videoTrack.applyConstraints({ advanced: [{ torch: next } as ExtendedTrackConstraintSet] })
            setTorchOn(next)
        } catch {
            // Torch control is experimental and not guaranteed on every device.
        }
    }, [torchOn])

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text)
        } catch {
            // Clipboard API may be unavailable (e.g. insecure context) - safe to ignore.
        }
    }

    // How much of the top of the screen is actually occupied by the app's fixed menu bar
    // (#logoHeader) and the question title/instructions block (#questionHeaderDetails) -
    // both live outside this component, and the question text's height varies with its
    // content and the device's screen width, so this has to be measured live rather than
    // guessed at with a fixed value.
    const [topOffsetPx, setTopOffsetPx] = useState(0)

    useEffect(() => {
        if (!showTarget) return

        const measure = () => {
            const headerHeight = document.getElementById('logoHeader')?.getBoundingClientRect().height || 0
            const questionHeaderHeight = document.getElementById('questionHeaderDetails')?.getBoundingClientRect().height || 0
            setTopOffsetPx(headerHeight + questionHeaderHeight)
        }

        measure()

        const elementsToWatch = [document.getElementById('logoHeader'), document.getElementById('questionHeaderDetails')]
            .filter((el): el is HTMLElement => !!el)
        const resizeObserver = new ResizeObserver(measure)
        elementsToWatch.forEach((el) => resizeObserver.observe(el))
        window.addEventListener('resize', measure)

        return () => {
            resizeObserver.disconnect()
            window.removeEventListener('resize', measure)
        }
    }, [showTarget])

    // Start scanning as soon as the camera is available, so the user doesn't
    // have to press "Start scanning" on first load.
    useEffect(() => {
        if (cameraSupported) {
            startScanning()
        }
    }, [cameraSupported, startScanning])

    // Release the camera when the component unmounts (e.g. the user switches tabs).
    // Resetting mountedRef true on mount matters under StrictMode, which runs
    // effects mount -> cleanup -> mount in dev; without this, the cleanup's
    // `false` would stick and the second mount's startScanning() would think
    // it was already unmounted, immediately stopping the camera it just opened.
    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
            if (intervalRef.current !== null) {
                window.clearInterval(intervalRef.current)
                intervalRef.current = null
            }
            streamRef.current?.getTracks().forEach((track) => track.stop())
            streamRef.current = null
        }
    }, [])

    return <div className={`w-screen h-screen fixed top-0 left-0 flex flex-col justify-center items-center`}>





        {(unsupportedReason || error) ? <div className="w-full h-full flex flex-col justify-center items-center gap-2">
            <div className="w-full h-full max-w-xl flex justify-center items-center">
                <RedAlert>{unsupportedReason || error}</RedAlert>
            </div>
        </div>
            :

            <div className="relative w-full h-full bg-black" style={{}}>
                <video
                    ref={videoRef}
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                    // Mirrored for display only, like the photo-question camera view - the
                    // decoder always reads from videoRef/the crop canvas directly, which are
                    // untouched by this CSS transform, so what gets decoded is never flipped.
                    style={isFrontFacing ? { transform: 'scaleX(-1)' } : undefined}
                />
                <canvas
                    ref={freezeCanvasRef}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ display: results ? 'block' : 'none', transform: isFrontFacing ? 'scaleX(-1)' : undefined }}
                />
                {!isScanning && !results && (
                    <div className="absolute inset-0 flex items-center justify-center text-white text-sm  text-center px-4">
                        <Spinner>{t('General.loading')}</Spinner>
                    </div>
                )}

                {showTarget && isScanning && !results && (
                    <div
                        className="absolute inset-0 grid pointer-events-none"
                        style={{
                            gridTemplateColumns: `${(viewportSize.width - targetSizeCssPx.width) / 2}px ${targetSizeCssPx.width}px ${(viewportSize.width - targetSizeCssPx.width) / 2}px`,
                            gridTemplateRows: `${(viewportSize.height - targetSizeCssPx.height) / 2}px ${targetSizeCssPx.height}px ${(viewportSize.height - targetSizeCssPx.height) / 2}px`,
                        }}
                    >
                        {Array.from({ length: 9 }).map((_, index) => (
                            <div key={index} className={index === 4 ? '' : 'bg-black/50'} />
                        ))}
                    </div>
                )}

                {showTarget && isScanning && !results && (
                    <div
                        className="absolute left-0 w-full bg-red-500/50 pointer-events-none"
                        style={{ top: '50%', height: '5px', transform: 'translateY(-50%)' }}
                    />
                )}

                {showTarget && targetIsSquare && isScanning && !results && (
                    <div
                        className="absolute top-0 h-full bg-red-500/50 pointer-events-none"
                        style={{ left: '50%', width: '5px', transform: 'translateX(-50%)' }}
                    />
                )}


                {results && <div className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center px-6">
                    <div className="w-full max-w-xl bg-white/70 rounded px-2 py-2">

                        <div className={`w-full flex flex-col items-center justify-center gap-0`}>
                            <p className={`font-righteous text-brandblue text-lg`}>{t('UIElements.BarCodeReader.detected')}</p>
                            <p className={`text-xs font-mono`}>{results.format}</p>
                            <p className={`w-full mt-2 bg-white px-3 py-2 rounded font-mono text-xs`}>{results.text}</p>
                        </div>

                        <div className={`w-full flex flex-row items-center justify-center gap-2 mt-2`}>
                            <Button
                                onClick={rescan}
                                text={t('UIElements.BarCodeReader.reset')}
                                variant={`primary`}
                                size={`small`}
                                fullwidth={false}
                            />
                        </div>

                    </div>
                </div>}

                {isScanning && !results && (
                    <div
                        className={showTarget
                            ? "absolute left-0 w-full flex flex-col items-center px-6"
                            : "absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center px-6"}
                        style={showTarget ? { top: `${topOffsetPx + 16}px` } : undefined}
                    >
                        <div className="w-full max-w-xl bg-white/70 rounded px-2 py-2">

                            <div className="flex items-center justify-center px-6 gap-2">
                                <p className={`animate-pulse text-3xl`}>🔎</p>
                                <p>{t('UIElements.BarCodeReader.lookingForBarcode')}</p>
                            </div>

                            {devices.length > 1 && <div className={`w-full flex flex-row items-center justify-center gap-2 mt-2`}>
                                <Button
                                    onClick={switchCameraToNextDevice}
                                    text={` 🔄 ${t('UIElements.Header.switchCamera')}    `}
                                    variant={`primary`}
                                    size={`small`}
                                    fullwidth={false}
                                />
                            </div>}

                        </div>
                    </div>
                )}


            </div>

        }




    </div>
}

export default BarCodeReader
